// Lens Module
import LensAPI from '@/api/lens'
import Fuse from 'fuse.js'
import Vue from 'vue'
import { providerKeyMap } from '@/utils/lens/dataSourceUtils'
import EventBus from '@/plugins/BaseNotifications/EventBus'

export default {
  namespaced: true,
  state: {
    // Metrics
    metricsPlatform: 'meta',
    metrics: {},
    metricLookup: {},
    metricGroups: {},
    isLoadingMetrics: false,

    // Search
    metricSearch: '',
    fuse: null,
    fuzzyResults: new Set(),

    // Presets
    presets: {},
    appliedPreset: {}, // used for the current session modifications
    localPreset: {}, // local preset for saving, apply changes to applied
    isCustomPreset: {},
    presetsLoaded: false,
  },
  getters: {
    // Metrics (Return only the metrics for the current platform)
    getMetricsPlatform: state => state.metricsPlatform,
    getMetrics: state => state.metrics[state.metricsPlatform],
    getMetricLookup: state => state.metricLookup[state.metricsPlatform],
    getMetricGroups: state => state.metricGroups[state.metricsPlatform],
    getMetricGroupsAllPlatforms: state => state.metricGroups,
    getIsLoadingMetrics: state => state.isLoadingMetrics,

    // Search
    getMetricSearch: state => state.metricSearch,
    getFuse: state => state.fuse[state.metricsPlatform],
    getFuzzyResults: state => state.fuzzyResults,

    // Presets (Return the appropriate preset for the current platform)
    getPresets: state => state.presets[state.metricsPlatform],
    getAppliedPreset: state => state.appliedPreset[state.metricsPlatform],
    getLocalPreset: state => state.localPreset[state.metricsPlatform],
    getIsCustomPreset: state => state.isCustomPreset[state.metricsPlatform] ?? true,
    getPresetsLoaded: state => state.presetsLoaded,
  },
  mutations: {
    SET_METRICS_PLATFORM (state, platform) {
      state.metricsPlatform = platform
    },
    SET_LENS_METRICS (state, metrics) {
      state.metrics = metrics
    },
    SET_METRIC_LOOKUP (state, lookup) {
      state.metricLookup = { ...lookup }
    },
    SET_METRIC_GROUPS (state, groups) {
      state.metricGroups = groups
    },
    SET_IS_LOADING_METRICS (state, isLoading) {
      state.isLoadingMetrics = isLoading
    },
    SET_METRIC_SEARCH (state, text) {
      state.metricSearch = text
    },
    SET_FUSE (state, fuse) {
      state.fuse = fuse
    },
    SET_FUZZY_RESULTS (state, results) {
      state.fuzzyResults = results
    },
    // Presets
    SET_PRESETS (state, presets) {
      state.presets = presets
    },
    SET_APPLIED_PRESET (state, preset) {
      state.appliedPreset = { ...preset }
    },
    SET_LOCAL_PRESET (state, preset) {
      state.localPreset = { ...preset }
    },
    SET_LOCAL_PRESET_VALUE (state, { key, value }) {
      Vue.set(state.localPreset[state.metricsPlatform], key, value)
    },
    SET_IS_CUSTOM_PRESET (state, isCustom) {
      state.isCustomPreset = isCustom
    },
    SET_PRESETS_LOADED (state, loaded) {
      state.presetsLoaded = loaded
    }
  },
  actions: {
    async fetchLensMetrics ({ state, commit }, metricPayload) {
      const { lensId, registryOverride } = metricPayload || {}
      commit('SET_IS_LOADING_METRICS', true)

      const platformMetrics = {}
      const platformMetricLookup = {}
      const platformMetricGroups = {}
      
      const platformMetricsData = await LensAPI.LensMetrics.getLensMetrics(lensId, registryOverride)
      Object.entries(platformMetricsData).forEach(([platform, metricData]) => {
        const { rawData, metrics, metricLookup, metricGroups } = metricData
        platformMetrics[platform] = metrics
        platformMetricLookup[platform] = metricLookup
        platformMetricGroups[platform] = metricGroups
        
        // Configure Fuse searcher
        try {
          if (!state.fuse?.[platform]) {
            // Initialize Fuse searcher & metric lookup for this platform
            const options = {
              includeScore: true,
              keys: ['key', 'name'],
              ignoreLocation: true,
              threshold: 0.3
            }
            const fuse = new Fuse(rawData, options)
            commit('SET_FUSE', { ...state.fuse, [platform]: fuse })
          } else {
            // If fuse is already initialized, update the metrics in the lookup for this platform
            state.fuse[platform].setCollection(rawData)
          }
        } catch (error) {
          console.error('Error initializing Fuse searcher & metric lookup for platform', platform, error)
        }
      })

      commit('SET_LENS_METRICS', platformMetrics)
      commit('SET_METRIC_LOOKUP', platformMetricLookup)
      commit('SET_METRIC_GROUPS', platformMetricGroups)
      commit('SET_IS_LOADING_METRICS', false)
    },
    setFuzzyResults ({ commit }, results) {
      commit('SET_FUZZY_RESULTS', results)
    },
    updateTextSearch ({ commit }, text) {
      commit('SET_METRIC_SEARCH', text)
    },
    setMetricsPlatform ({ state, commit }, platform) {
      const oldPlatform = state.metricsPlatform
      commit('SET_METRICS_PLATFORM', platform)
      if (oldPlatform !== platform) {
        try {
          EventBus?.$emit?.('showPlatformChange', platform)
        } catch (e) {}
      }
    },
    // ======== PRESETS ========
    setLocalPreset({ state, commit }, preset) {
      commit('SET_LOCAL_PRESET', {
        ...state.localPreset,
        [state.metricsPlatform]: _.cloneDeep(preset)
      })
    },
    setAppliedPreset({ state, commit }, preset) {
      commit('SET_APPLIED_PRESET', {
        ...state.appliedPreset,
        [state.metricsPlatform]: _.cloneDeep(preset)
      })
    },
    updateIsCustomPreset({ state, commit }, isCustom) {
      commit('SET_IS_CUSTOM_PRESET', {
        ...state.isCustomPreset,
        [state.metricsPlatform]: isCustom
      })
    },
    async fetchPresets({ commit, rootGetters }, lensId = null) {
      const activeLensId = rootGetters['LensModule/getActiveLensId']
      const reqLensId = lensId ?? activeLensId

      const res = await LensAPI.LensPreset.getAll(reqLensId)

      const processPresets = (presets) => {
        // Merge in the lens-specific metrics for each preset (if applicable)
        return presets.map(preset => {
          const tableColumns = [...preset.table_columns]
          const lensSpecificColumns = [...(preset?.lens_specific_columns?.[reqLensId] ?? [])]
          lensSpecificColumns.forEach(({ key, index }) => {
            // Attempt to insert the lens-specific column into its specified index
            tableColumns.splice(index, 0, key)
          })
          return {
            ...preset,
            table_columns: [...new Set(tableColumns)]
          }
        })
      }

      const platformPresets = Object.fromEntries(Object.keys(providerKeyMap).map(platform => [platform, []]))
      const presetToSelect = Object.fromEntries(Object.keys(providerKeyMap).map(platform => [platform, null]))
      console.log('PROCESSING PRESETS', res.data)
      Object.entries(res.data).forEach(([platform, presets]) => {
        const processedPresets = processPresets(presets)
        platformPresets[platform] = processedPresets
        presetToSelect[platform] = processedPresets?.find(preset => preset.is_default) ?? processedPresets?.[0]
      })
      console.log('PLATFORM PRESETS', platformPresets)

      commit('SET_PRESETS', platformPresets)
      commit('SET_APPLIED_PRESET', _.cloneDeep(presetToSelect))
      commit('SET_LOCAL_PRESET', _.cloneDeep(presetToSelect))
      commit('SET_PRESETS_LOADED', true)
    },
    /**
     * -- shouldSelect = true --
     * Used when a user changes presets for a report
     * OR
     * -- shouldSelect = false --
     * Used when a user is swapping between presets for editing
     */
    async setPresetById({ dispatch, state }, { id, shouldSelect = false }) {
      const presets = state.presets[state.metricsPlatform]
      const selectedPreset = (id ? presets.find((preset) => preset.id === id) : presets?.[0]) ?? {}
      
      dispatch('setLocalPreset', selectedPreset)
      if (shouldSelect) {
        dispatch('setAppliedPreset', selectedPreset)
      }
    },
    async updatePreset({ dispatch, state, rootGetters }, toUpdate) {
      const activeLensId = rootGetters['LensModule/getActiveLensId']
      const metricLookup = rootGetters['MetricsModule/getMetricLookup']

      // Split up the table columns into global and lens specific metrics
      let globalMetrics = []
      let lensSpecificMetrics = []
      toUpdate?.table_columns?.forEach((key, index) => {
        if (metricLookup?.[key]?.is_lens_specific) lensSpecificMetrics.push({ key, index })
        else if (metricLookup?.[key]) globalMetrics.push(key)
      })

      // Ensure uniqueness in both arrays
      globalMetrics = [...new Set(globalMetrics)]
      lensSpecificMetrics = [...new Map(lensSpecificMetrics.map(metric => [metric.key, metric])).values()]

      // Store the selected lens-specific metrics by associating them with the active lens id
      const newLensSpecificMetrics = toUpdate?.lens_specific_columns ?? {}
      if (activeLensId) {
        newLensSpecificMetrics[activeLensId] = lensSpecificMetrics
      }

      const updatePayload = {
        ...toUpdate,
        table_columns: globalMetrics,
        lens_specific_columns: newLensSpecificMetrics,
        provider: providerKeyMap[state.metricsPlatform]
      }
      try {
        await LensAPI.LensPreset.update(toUpdate.id, updatePayload)
      } catch (e) {
        console.error(e)
      }

      const originalPreset = state.localPreset[state.metricsPlatform]
      dispatch('setLocalPreset', { ...originalPreset, ...toUpdate })
      await dispatch('fetchPresets')
    },
    async createPreset({ state, dispatch, rootGetters }, toSave) {
      const activeLensId = rootGetters['LensModule/getActiveLensId']
      const metricLookup = rootGetters['MetricsModule/getMetricLookup']

      // Split up the table columns into global and lens specific metrics
      let globalMetrics = []
      let lensSpecificMetrics = []
      toSave?.table_columns?.forEach((key, index) => {
        if (metricLookup?.[key]?.is_lens_specific) lensSpecificMetrics.push({ key, index })
        else if (metricLookup?.[key]) globalMetrics.push(key)
      })

      // Ensure uniqueness in both arrays
      globalMetrics = [...new Set(globalMetrics)]
      lensSpecificMetrics = [...new Map(lensSpecificMetrics.map(metric => [metric.key, metric])).values()]

      const createPayload = {
        ...toSave,
        table_columns: globalMetrics,
        ...(lensSpecificMetrics.length ? { lens_specific_columns: { [activeLensId]: lensSpecificMetrics } } : {}),
        provider: providerKeyMap[state.metricsPlatform]
      }

      try {
        const { data } = await LensAPI.LensPreset.create(createPayload)
        await dispatch('fetchPresets')
        return data.id
      } catch (e) {
        console.error(e)
        return null
      }
    },
    applyPreset({ dispatch }, toUpdate) {
      dispatch('setLocalPreset', toUpdate)
      dispatch('setAppliedPreset', toUpdate)
    },
    updateLocalPresetField ({ commit }, { key, value }) {
      commit('SET_LOCAL_PRESET_VALUE', { key, value })
    }
  }
}
